import type { PublishRawTagList, PublishTagSetupState } from '@/publish/types'
import { PROJECT_CURRENT_TAG_DRAFT } from '@/features/project-cloud/ossPaths'
import BrowserUtil from '@/platform/oss/browserUtil'

const TAG_SETUP_DRAFT_PREFIX = 'avgmaker:publish:tag-draft:'

function buildDraftStorageKey(guid: string) {
  return `${TAG_SETUP_DRAFT_PREFIX}${guid.trim()}`
}

export type StoredTagDraft = {
  selection: PublishRawTagList
  state: PublishTagSetupState
}

function clonePayload<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function buildOssDraftPath(guid: string): string {
  return `${guid.trim()}/${PROJECT_CURRENT_TAG_DRAFT}`
}

function normalizeState(value: unknown): PublishTagSetupState {
  return value === 'submitted' ? 'submitted' : value === 'staged' ? 'staged' : 'none'
}

function buildDraftPayload(guid: string, uid: number | string | null | undefined, draft: StoredTagDraft): string {
  return JSON.stringify({
    ...clonePayload(draft.selection),
    guid: draft.selection.guid || guid,
    uid: draft.selection.uid ?? uid ?? '',
  })
}

function parseDraftPayload(raw: string): StoredTagDraft | null {
  try {
    const parsed = JSON.parse(raw) as PublishRawTagList
    return {
      selection: clonePayload(parsed),
      state: 'staged',
    }
  } catch {
    return null
  }
}

function saveLocalTagDraft(guid: string, draft: StoredTagDraft) {
  const normalizedGuid = guid.trim()
  if (!normalizedGuid) return
  localStorage.setItem(
    buildDraftStorageKey(normalizedGuid),
    JSON.stringify({
      selection: clonePayload(draft.selection),
      state: draft.state,
    }),
  )
}

function readLocalTagDraft(guid: string): StoredTagDraft | null {
  const normalizedGuid = guid.trim()
  if (!normalizedGuid) return null
  const raw = localStorage.getItem(buildDraftStorageKey(normalizedGuid))
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as {
      selection?: PublishRawTagList
      state?: PublishTagSetupState
    }
    const selection = parsed.selection ? clonePayload(parsed.selection) : null
    if (!selection) return null
    return {
      selection,
      state: normalizeState(parsed.state),
    }
  } catch {
    return null
  }
}

function clearLocalTagDraft(guid: string) {
  const normalizedGuid = guid.trim()
  if (!normalizedGuid) return
  localStorage.removeItem(buildDraftStorageKey(normalizedGuid))
}

export async function saveStoredTagDraft(guid: string, uid: number | string | null | undefined, draft: StoredTagDraft) {
  const normalizedGuid = guid.trim()
  if (!normalizedGuid) return
  saveLocalTagDraft(normalizedGuid, draft)
  const oss = BrowserUtil.getInstance()
  if (!oss.initDefaultConfig().isConfigured()) return
  if (uid !== undefined && uid !== null && String(uid).trim()) {
    oss.setRootDir(String(uid).trim())
  }
  await oss.writeData(buildOssDraftPath(normalizedGuid), buildDraftPayload(normalizedGuid, uid, draft), {
    contentType: 'application/json;charset=utf-8',
  })
}

export async function readStoredTagDraft(guid: string, uid?: number | string | null): Promise<StoredTagDraft | null> {
  const normalizedGuid = guid.trim()
  if (!normalizedGuid) return null
  const oss = BrowserUtil.getInstance()
  if (oss.initDefaultConfig().isConfigured()) {
    try {
      if (uid !== undefined && uid !== null && String(uid).trim()) {
        oss.setRootDir(String(uid).trim())
      }
      if (await oss.hasObject(buildOssDraftPath(normalizedGuid))) {
        const draft = parseDraftPayload(await oss.getText(buildOssDraftPath(normalizedGuid)))
        if (draft) {
          saveLocalTagDraft(normalizedGuid, draft)
          return draft
        }
      }
      clearLocalTagDraft(normalizedGuid)
      return null
    } catch {
    }
  }
  return readLocalTagDraft(normalizedGuid)
}

export async function clearStoredTagDraft(guid: string, uid?: number | string | null) {
  const normalizedGuid = guid.trim()
  if (!normalizedGuid) return
  clearLocalTagDraft(normalizedGuid)
  const oss = BrowserUtil.getInstance()
  if (!oss.initDefaultConfig().isConfigured()) return
  try {
    if (uid !== undefined && uid !== null && String(uid).trim()) {
      oss.setRootDir(String(uid).trim())
    }
    if (await oss.hasObject(buildOssDraftPath(normalizedGuid))) {
      await oss.deleteFile(buildOssDraftPath(normalizedGuid))
    }
  } catch {
  }
}
